import { http } from "./http";

/**
 * Combined overview row backing the Schedules list page. `kind` tells the
 * SPA whether the row is a weekplan (7-day × 15-minute open/closed mask)
 * or a calendar (dated exceptions in a named timezone). The corresponding
 * edit URL is `/schedules/weekplans/:id` or `/schedules/calendars/:id`.
 *
 * `isLive` is a transient server-side bit:
 *   - weekplan: true when the current 15-minute slot on today's day-mask
 *     is "1" (open),
 *   - calendar: true when today (in the calendar's own timezone) is a
 *     marked date.
 */
export type ScheduleKind = "Weekplan" | "Calendar";

export interface ScheduleListItem {
  kind: ScheduleKind;
  id: number;
  name: string;
  description: string | null;
  isLive: boolean;
}

export interface ScheduleListResponse {
  items: ScheduleListItem[];
}

export interface ScheduleCreated {
  kind: ScheduleKind;
  id: number;
  name: string;
}

/**
 * Seven day-masks, one per weekday. Each string is exactly 96 characters
 * long — one "1" or "0" per 15-minute slot, starting at 00:00 and ending
 * at 23:45. The UI works in HH:mm ranges, so helpers on the weekplan
 * detail page convert ranges <-> masks; the API takes and returns masks
 * verbatim.
 */
export interface WeekplanDayMasks {
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
  sun: string;
}

export interface WeekplanDetail {
  weekplanId: number;
  name: string;
  masks: WeekplanDayMasks;
}

export interface CalendarDetail {
  calendarId: number;
  name: string;
  description: string;
  /**
   * Raw timezone identifier (matches `sys.time_zone_info.name`, typically a
   * Windows-style zone like "W. Europe Standard Time"). `null` means the
   * calendar inherits the tenant default; the picker shows "Default (—)"
   * in that case.
   */
  timezoneId: string | null;
  /** Formatted timezone label, e.g. "W. Europe Standard Time (currently UTC+01:00)". */
  timezone: string;
}

/** One option in the timezone picker. */
export interface TimezoneOption {
  id: string;
  /** Pre-formatted label including a static offset, e.g. "… (UTC+01:00)". */
  label: string;
  /** Minutes east of UTC. Used client-side to group options by region. */
  offsetMinutes: number;
}

export interface TimezonesResponse {
  items: TimezoneOption[];
}

export interface CalendarItemsResponse {
  calendarId: number;
  /** Array of ISO dates ("yyyy-MM-dd"), sorted ascending. */
  dates: string[];
}

export const schedulesApi = {
  list: () => http<ScheduleListResponse>("/api/schedules"),
  create: (payload: { kind: ScheduleKind; name: string }) =>
    http<ScheduleCreated>("/api/schedules", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // weekplan
  getWeekplan: (weekplanId: number) =>
    http<WeekplanDetail>(`/api/schedules/weekplans/${weekplanId}`),
  updateWeekplan: (
    weekplanId: number,
    payload: { name: string; masks: WeekplanDayMasks },
  ) =>
    http<WeekplanDetail>(`/api/schedules/weekplans/${weekplanId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteWeekplan: (weekplanId: number) =>
    http<void>(`/api/schedules/weekplans/${weekplanId}`, { method: "DELETE" }),

  // calendar
  getCalendar: (calendarId: number) =>
    http<CalendarDetail>(`/api/schedules/calendars/${calendarId}`),
  updateCalendar: (
    calendarId: number,
    payload: {
      name: string;
      description: string;
      /**
       * Timezone id to write. Leave `undefined` to skip the timezone
       * update (back-compat); pass `""` to clear the override; pass a
       * concrete id to set it.
       */
      timezoneId?: string;
    },
  ) =>
    http<CalendarDetail>(`/api/schedules/calendars/${calendarId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteCalendar: (calendarId: number) =>
    http<void>(`/api/schedules/calendars/${calendarId}`, { method: "DELETE" }),

  listCalendarItems: (calendarId: number) =>
    http<CalendarItemsResponse>(`/api/schedules/calendars/${calendarId}/items`),
  addCalendarItem: (calendarId: number, isoDate: string) =>
    http<CalendarItemsResponse>(
      `/api/schedules/calendars/${calendarId}/items`,
      {
        method: "POST",
        body: JSON.stringify({ date: isoDate }),
      },
    ),
  deleteCalendarItem: (calendarId: number, isoDate: string) =>
    http<CalendarItemsResponse>(
      `/api/schedules/calendars/${calendarId}/items?date=${encodeURIComponent(
        isoDate,
      )}`,
      { method: "DELETE" },
    ),

  // timezone picker
  listTimezones: () => http<TimezonesResponse>("/api/schedules/timezones"),
};
